import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Video, Plus, Trash2, Radio, Clock, CalendarDays, Info } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getStatus = (lesson) => {
  const start = new Date(lesson.scheduled_at).getTime();
  const end = start + lesson.duration_minutes * 60000;
  const now = Date.now();
  if (now < start) return 'upcoming';
  if (now <= end) return 'live';
  return 'ended';
};

const StatusBadge = ({ status }) => {
  if (status === 'live') {
    return (
      <Badge className="bg-red-500 hover:bg-red-500 text-white animate-pulse">
        <Radio className="h-3 w-3 mr-1" /> НА ЖИВО
      </Badge>
    );
  }
  if (status === 'upcoming') {
    return <Badge className="bg-[#00966E] hover:bg-[#00966E] text-white">Предстоящ</Badge>;
  }
  return <Badge variant="outline" className="text-slate-400 border-slate-300">Приключил</Badge>;
};

export const OnlineLessonsPanel = ({ role }) => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [classes, setClasses] = useState([]);
  const [title, setTitle] = useState('');
  const [classId, setClassId] = useState('');
  const [dateTime, setDateTime] = useState('');
  const [duration, setDuration] = useState('40');
  const [creating, setCreating] = useState(false);

  const canManage = role === 'teacher' || role === 'administrator';
  const authHeaders = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const fetchData = useCallback(async () => {
    try {
      const [lessonsRes, classesRes] = await Promise.all([
        axios.get(`${API_URL}/online-lessons`, authHeaders()),
        canManage ? axios.get(`${API_URL}/classes`, authHeaders()) : Promise.resolve({ data: [] }),
      ]);
      setLessons(lessonsRes.data);
      setClasses(classesRes.data);
    } catch {
      toast.error('Грешка при зареждане на онлайн часовете');
    }
  }, [authHeaders, canManage]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!title.trim() || !classId || !dateTime) {
      toast.error('Попълни заглавие, клас и дата/час');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API_URL}/online-lessons`, {
        title: title.trim(),
        class_id: classId,
        scheduled_at: new Date(dateTime).toISOString(),
        duration_minutes: Number(duration),
      }, authHeaders());
      toast.success('Онлайн часът е създаден');
      setTitle(''); setDateTime('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Грешка при създаване');
    } finally {
      setCreating(false);
    }
  };

  const handleJoin = async (lesson) => {
    // If the current user is the owning teacher (or admin), notify the class first
    const isOwner = role === 'administrator' || (role === 'teacher' && lesson.teacher_id === user?.user_id);
    if (isOwner) {
      try {
        await axios.post(`${API_URL}/online-lessons/${lesson.lesson_id}/start`, {}, authHeaders());
      } catch {
        // Non-blocking: joining should still work even if broadcast fails
      }
    }
    navigate(`/classroom/${lesson.lesson_id}`);
  };

  const handleDelete = async (lessonId) => {
    if (!window.confirm('Изтриване на този онлайн час?')) return;
    try {
      await axios.delete(`${API_URL}/online-lessons/${lessonId}`, authHeaders());
      toast.success('Онлайн часът е изтрит');
      setLessons((prev) => prev.filter((l) => l.lesson_id !== lessonId));
    } catch {
      toast.error('Грешка при изтриване');
    }
  };

  const sorted = [...lessons].sort((a, b) => {
    const order = { live: 0, upcoming: 1, ended: 2 };
    const diff = order[getStatus(a)] - order[getStatus(b)];
    return diff !== 0 ? diff : new Date(a.scheduled_at) - new Date(b.scheduled_at);
  });

  return (
    <div className="space-y-6" data-testid="online-lessons-panel">
      {canManage && (
        <Card className="p-4 sm:p-6">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-4">
            <Plus className="h-4 w-4 text-[#00966E]" /> Нов виртуален час
          </h3>
          <form onSubmit={handleCreate} data-testid="create-online-lesson-form" className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
            <div className="lg:col-span-2">
              <Label htmlFor="ol-title">Тема на часа</Label>
              <Input
                id="ol-title"
                data-testid="online-lesson-title-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="напр. Български език — урок 12"
                maxLength={120}
              />
            </div>
            <div>
              <Label>Клас</Label>
              <Select value={classId} onValueChange={setClassId}>
                <SelectTrigger data-testid="online-lesson-class-select">
                  <SelectValue placeholder="Избери клас" />
                </SelectTrigger>
                <SelectContent>
                  {classes.map((cls) => (
                    <SelectItem key={cls.class_id} value={cls.class_id}>{cls.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="ol-datetime">Дата и час</Label>
              <Input
                id="ol-datetime"
                data-testid="online-lesson-date-input"
                type="datetime-local"
                value={dateTime}
                onChange={(e) => setDateTime(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <Label>Минути</Label>
                <Select value={duration} onValueChange={setDuration}>
                  <SelectTrigger data-testid="online-lesson-duration-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['20', '30', '40', '60', '90', '120'].map((d) => (
                      <SelectItem key={d} value={d}>{d} мин</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="submit"
                data-testid="create-online-lesson-btn"
                disabled={creating}
                className="bg-[#00966E] hover:bg-[#007a59] text-white"
              >
                <Video className="h-4 w-4 mr-1" /> Създай
              </Button>
            </div>
          </form>
          <p className="text-xs text-slate-400 mt-3 flex items-start gap-1.5">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            Като водещ на часа влизате директно в стаята и ставате модератор. Учениците също влизат без акаунт. За повече сигурност можете да включите „Заключване на стаята" от бутона за защита в стаята.
          </p>
        </Card>
      )}

      <div className="space-y-3">
        {sorted.length === 0 ? (
          <Card className="p-8 text-center" data-testid="no-online-lessons">
            <Video className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">Няма насрочени онлайн часове</p>
            {!canManage && <p className="text-sm text-slate-400 mt-1">Когато учител насрочи виртуален час за твоя клас, ще го видиш тук.</p>}
          </Card>
        ) : (
          sorted.map((lesson) => {
            const status = getStatus(lesson);
            const dt = new Date(lesson.scheduled_at);
            return (
              <Card key={lesson.lesson_id} data-testid={`online-lesson-card-${lesson.lesson_id}`} className={`p-4 ${status === 'live' ? 'border-red-300 bg-red-50/40' : status === 'ended' ? 'opacity-60' : ''}`}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${status === 'live' ? 'bg-red-100' : 'bg-emerald-50'}`}>
                    <Video className={`h-5 w-5 ${status === 'live' ? 'text-red-500' : 'text-[#00966E]'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="font-semibold text-slate-800 truncate">{lesson.title}</h4>
                      <StatusBadge status={status} />
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 mt-1">
                      <span className="font-medium text-[#00966E]">{lesson.class_name}</span>
                      <span className="inline-flex items-center gap-1">
                        <CalendarDays className="h-3 w-3" />
                        {dt.toLocaleDateString('bg-BG', { day: 'numeric', month: 'long' })}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {dt.toLocaleTimeString('bg-BG', { hour: '2-digit', minute: '2-digit' })} · {lesson.duration_minutes} мин
                      </span>
                      <span>Учител: {lesson.teacher_name}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {status !== 'ended' && (
                      <Button
                        data-testid={`join-online-lesson-btn-${lesson.lesson_id}`}
                        onClick={() => handleJoin(lesson)}
                        className={status === 'live' ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-[#00966E] hover:bg-[#007a59] text-white'}
                      >
                        <Video className="h-4 w-4 mr-1.5" />
                        {status === 'live' ? 'Влез сега' : 'Влез в стаята'}
                      </Button>
                    )}
                    {(role === 'administrator' || (role === 'teacher' && lesson.teacher_id === user?.user_id)) && (
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid={`delete-online-lesson-btn-${lesson.lesson_id}`}
                        onClick={() => handleDelete(lesson.lesson_id)}
                        className="text-slate-400 hover:text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};
